import { createContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useGetGQLClientForProject } from "hooks/init-gql-client";

import jwtDecode from "jwt-decode";
import { NotFoundPage } from "pages";
import dayjs from "utils/dayjs-config";
import { message } from "antd";
import {
  getSubmittalIntegrationConfig,
  simplifyProjectIntegrationConfigs
} from "pages/subscription-settings/integrations-tab-new/utils";
import useChangeEventPolling, {
  TAuditChangeLog
} from "change-events/change-event-polling";
import { useProjectGlobalDataSource } from "hooks/project-global-data";
import { isPermissionNotGranttedUtil } from "utils/permissions";
import { setCurrentProjectArchived } from "utils/project-archive-state";
import { getHasuraClaims } from "utils/jwt-utils";
import { setDsmProjectToken } from "design-scope/dsm-auth";
import { useUserProjectProfileInfo } from "hooks/users";
import { syncDateblockViewModeForProject } from "entity-app/shared-components/entity-dateblock/dateblock-view-mode-toggle";
import { EUserRoleName, EUserTypes, ErrorMessages } from "../constants";

export interface ProjectProviderProps {
  children: React.ReactNode;
}

export type ProjectSettingType = {
  id: string;
  mode: number;
  spec_section: any;
  name: string;
  timezone: {
    timezone_id: string;
    name: string;
  };
  new_entity_model_enabled?: boolean;
  project_phase?: {
    id: number;
    value: string;
  } | null;
};

export type TProjectContext = {
  projectToken: any;
  gqlClientForProject: any;
  tokenRetrievalState: any;
  tokenContents?: {
    userType: string;
    role: string;
    claims: any;
  };
  calHolidaysWeekends?: { holidays: Array<any>; weekends: Array<any> };
  columnHeaders: {
    submittalHeaders: any;
    materialHeaders: any;
    submittalHeaderMap: any;
    submittalCustomColumns: any;
    materialHeaderMap: any;
    materialCustomColumns: any;
    bidPackageHeaders: any;
    designPackageHeaders: any;
    bidPackageHeaderMap: any;
    designPackageHeaderMap: any;
    designPackageCustomColumns: any;
    bidPackageCustomColumns: any;
    designScopeHeaders: any;
    designScopeHeaderMap: any;
  };
  projectDetails: ProjectSettingType | undefined;
  projectDetailsQuerySettled: boolean;
  projectDetailsFailed: boolean;
  projectIntegrationConfig: any;
  eventLogs: Array<TAuditChangeLog>;
  lastImportLogData: any;
  isFeatureFlagEnabled: (feature: string | undefined) => boolean;
  isProductEntitlementsDataAvailable: boolean;
  isProjectMember?: boolean;
  isProcoreProject?: boolean;
  /** Archived projects are read-only; write permissions are denied. */
  isProjectArchived?: boolean;
  loggedInUserProjectProfileResponse?: any;
  metadata?: any;
};

export const ProjectContext = createContext<TProjectContext>({
  projectToken: null,
  gqlClientForProject: null,
  tokenRetrievalState: null,
  tokenContents: undefined,
  calHolidaysWeekends: { holidays: [], weekends: [] },
  columnHeaders: {
    submittalHeaders: null,
    materialHeaders: null,
    submittalHeaderMap: {},
    submittalCustomColumns: [],
    materialHeaderMap: {},
    materialCustomColumns: [],
    bidPackageHeaders: null,
    designPackageHeaders: null,
    bidPackageHeaderMap: {},
    designPackageHeaderMap: {},
    designPackageCustomColumns: [],
    bidPackageCustomColumns: [],
    designScopeHeaders: null,
    designScopeHeaderMap: {}
  },
  projectDetails: undefined,
  projectDetailsQuerySettled: false,
  projectDetailsFailed: false,
  projectIntegrationConfig: null,
  eventLogs: [],
  lastImportLogData: null,
  isFeatureFlagEnabled: () => false,
  isProductEntitlementsDataAvailable: false,
  isProjectMember: false,
  isProcoreProject: false,
  loggedInUserProjectProfileResponse: {
    loading: false,
    error: null,
    data: undefined
  },
  metadata: {}
});

const getUserTypeFromToken = (token: string) => {
  const hasuraClaims = getHasuraClaims(token);
  return (
    hasuraClaims?.["x-hasura-user-type"] ||
    EUserTypes[EUserTypes.GENERAL_CONTRACTOR]
  );
};

const getProjectRoleFromToken = (token: string) => {
  const hasuraClaims = getHasuraClaims(token);
  return (
    hasuraClaims?.["x-hasura-default-role"] ||
    EUserRoleName[EUserRoleName.owner]
  );
};

const isCurrentProjectMember = (token: string) => {
  const decryptedToken: any = jwtDecode(token);
  try {
    return decryptedToken?.isProjectMember;
  } catch {
    // Unreachable: optional-chained property read on the decoded token cannot throw
    /* v8 ignore next */
    return false;
  }
};

const isProcoreProject = (projectIntegrationConfig: any) => {
  // Not [0]: a Procore project that also carries an OPC schedule config would
  // otherwise stop reading as one whenever OPC won that slot, taking the
  // submittal log's Recycle Bin and Delete controls with it.
  const systemIntgConfig = getSubmittalIntegrationConfig(
    projectIntegrationConfig
  )?.systemIntgConfigProperties;

  return systemIntgConfig?.DATA_SOURCE_NAME === "Procore";
};

const getUserClaimsFromToken = (token: string) => {
  const hasuraClaims = getHasuraClaims(token);
  return hasuraClaims;
};

export const isPermissionNotGrantted = isPermissionNotGranttedUtil;

function ProjectProvider({ children }: ProjectProviderProps) {
  const [projectPermissionAccess, setProjectPermissionAccess] = useState(true);
  const [projectState, setProjectState] = useState<TProjectContext>({
    projectToken: null,
    gqlClientForProject: null,
    tokenRetrievalState: null,
    columnHeaders: {
      submittalHeaders: null,
      materialHeaders: null,
      submittalHeaderMap: {},
      submittalCustomColumns: [],
      materialHeaderMap: {},
      materialCustomColumns: [],
      bidPackageHeaders: null,
      designPackageHeaders: null,
      bidPackageHeaderMap: {},
      designPackageHeaderMap: {},
      designPackageCustomColumns: [],
      bidPackageCustomColumns: [],
      designScopeHeaders: null,
      designScopeHeaderMap: {}
    },
    projectDetails: undefined,
    projectDetailsQuerySettled: false,
    projectDetailsFailed: false,
    projectIntegrationConfig: null,
    eventLogs: [],
    lastImportLogData: null,
    isFeatureFlagEnabled: () => false,
    isProductEntitlementsDataAvailable: false,
    isProjectMember: false,
    isProcoreProject: false,
    loggedInUserProjectProfileResponse: {
      loading: true,
      error: null,
      data: undefined
    },
    metadata: {}
  });
  const { projectId } = useParams() as any;

  useEffect(() => {
    if (projectId) {
      syncDateblockViewModeForProject(projectId);
    }
  }, [projectId]);

  const { gqlClientForProject, tokenRetrievalState, projectToken } =
    useGetGQLClientForProject(projectId);

  const { data: eventLogs } = useChangeEventPolling({
    gqlClient: gqlClientForProject,
    projectId
  });

  const {
    projectDetails: projectDetailsData,
    projectDetailsLoading,
    projectDetailsError,
    calendarOffs,
    columnHeaders,
    projectIntgConfigs,
    lastImportStatus,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    isProjectArchived,
    isArchivedStatusResolved
  } = useProjectGlobalDataSource(eventLogs, gqlClientForProject, { projectId });

  useEffect(() => {
    return () => setCurrentProjectArchived(false);
  }, []);

  const {
    data: loggedInUserProjectProfileData,
    loading: loggedInUserProjectProfileLoading,
    error: loggedInUserProjectProfileError
  } = useUserProjectProfileInfo(gqlClientForProject);

  // Effect 1: Sync data to state (eventLogs, lastImportStatus, projectIntegrationConfig)
  useEffect(() => {
    const integrationConfig = simplifyProjectIntegrationConfigs(
      projectIntgConfigs?.project_integration_configs
    );

    setProjectState((prev: TProjectContext) => ({
      ...prev,
      eventLogs,
      lastImportLogData: lastImportStatus,
      projectIntegrationConfig: integrationConfig,
      isProcoreProject: isProcoreProject(integrationConfig)
    }));
  }, [
    eventLogs,
    lastImportStatus,
    projectIntgConfigs?.project_integration_configs
  ]);

  // Effect 2: Handle project details and timezone
  useEffect(() => {
    const project = projectDetailsData?.project_by_pk;
    const settled =
      !projectDetailsLoading &&
      (projectDetailsData !== undefined || Boolean(projectDetailsError));

    if (project) {
      try {
        dayjs.tz.setDefault(project.timezone.timezone_id);
      } catch {
        message.error(ErrorMessages.TimezoneFetchFailed);
      }
    }

    setProjectState((pre) => {
      const nextDetails =
        settled && !project ? undefined : project || pre.projectDetails;
      const failed = settled && !project;
      if (
        pre.projectDetails === nextDetails &&
        pre.projectDetailsQuerySettled === settled &&
        pre.projectDetailsFailed === failed
      ) {
        return pre;
      }
      return {
        ...pre,
        projectDetails: nextDetails,
        projectDetailsQuerySettled: settled,
        projectDetailsFailed: failed
      };
    });
  }, [projectDetailsData, projectDetailsError, projectDetailsLoading]);

  // Effect 3: Token and authentication handling
  useEffect(() => {
    if (tokenRetrievalState.error === true) {
      setProjectPermissionAccess(false);
    }
    if (
      projectId &&
      gqlClientForProject &&
      tokenRetrievalState.success &&
      tokenRetrievalState.token &&
      // Permissions are computed from tokenContents, so hold it back until the
      // archived flag is known; otherwise an archived project briefly renders
      // with write actions enabled.
      isArchivedStatusResolved
    ) {
      setCurrentProjectArchived(!!isProjectArchived);
      setProjectPermissionAccess(true);
      setProjectState((pre) => ({
        ...pre,
        isProjectArchived: !!isProjectArchived,
        projectToken,
        gqlClientForProject,
        tokenRetrievalState,
        tokenContents: {
          userType: getUserTypeFromToken(tokenRetrievalState.token),
          role: getProjectRoleFromToken(tokenRetrievalState.token),
          claims: getUserClaimsFromToken(tokenRetrievalState.token)
        },
        calHolidaysWeekends: calendarOffs,
        columnHeaders: {
          ...pre.columnHeaders,
          ...columnHeaders
        },
        isFeatureFlagEnabled,
        isProductEntitlementsDataAvailable,
        isProjectMember: isCurrentProjectMember(tokenRetrievalState.token),
        loggedInUserProjectProfileResponse: {
          data: loggedInUserProjectProfileData,
          loading: loggedInUserProjectProfileLoading,
          error: loggedInUserProjectProfileError
        }
      }));

      (window as any).projectToken = tokenRetrievalState.token; // for dev purposes
      // Hand the project token to the plain dsmApi module (DSM writes need the
      // project-scoped token, which isn't reachable from services/auth).
      setDsmProjectToken(tokenRetrievalState.token);
    }
  }, [
    projectToken,
    projectId,
    gqlClientForProject,
    tokenRetrievalState,
    calendarOffs,
    columnHeaders,
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable,
    loggedInUserProjectProfileData,
    loggedInUserProjectProfileLoading,
    loggedInUserProjectProfileError,
    isProjectArchived,
    isArchivedStatusResolved
  ]);

  return (
    <ProjectContext.Provider value={projectState}>
      {projectPermissionAccess ? (
        children
      ) : (
        <NotFoundPage
          status="403"
          title="403"
          subTitle="Sorry, you are not authorized to access this page."
        />
      )}
    </ProjectContext.Provider>
  );
}

export default ProjectProvider;
